/**
 * ThemePicker Component
 * 
 * A dropdown selector for choosing visual themes.
 * Integrates with pageLayoutSettingsSlice for state management.
 * Persists preference to database for authenticated users.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setTheme } from '../../slices/pageLayoutSettingsSlice.mjs';
import axios from 'axios';

// Available themes configuration
const AVAILABLE_THEMES = [
    { name: 'default', label: 'Default', icon: '🌐', description: 'Classic professional theme' },
    { name: 'court-side', label: 'Court Side', icon: '🏀', description: 'Premium dark with amber glow' },
    { name: 'full-court-press', label: 'Full Court Press', icon: '⚡', description: 'Intense electric blue' },
    { name: 'hardwood', label: 'Hardwood', icon: '🌳', description: 'Classic warm wood tones' },
    { name: 'triple-double', label: 'Triple Double', icon: '🏆', description: 'Elite gold on black' },
    { name: 'downtown', label: 'Downtown', icon: '🌃', description: 'Neon synthwave vibes' },
    { name: 'buzzer-beater', label: 'Buzzer Beater', icon: '⏱️', description: 'Urgent red intensity' },
];

export default function ThemePicker({ className = '' }) {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Get current theme from Redux store
    const resolvedTheme = useSelector(state => state.pageLayoutSettings?.resolvedTheme);
    const currentThemeName = resolvedTheme?.name || 'default';
    const currentTheme = AVAILABLE_THEMES.find(t => t.name === currentThemeName) || AVAILABLE_THEMES[0];

    // Get auth state to determine if we should persist to DB
    const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleThemeSelect = async (theme) => {
        // Update Redux state
        dispatch(setTheme({ name: theme.name, version: theme.versions?.[0] || null }));

        // Apply theme to HTML element immediately (client-side)
        if (typeof document !== 'undefined') {
            const html = document.documentElement;
            if (theme.name === 'default') {
                html.removeAttribute('data-theme');
                html.removeAttribute('data-theme-version');
            } else {
                html.setAttribute('data-theme', theme.name);
                if (theme.versions?.[0]) {
                    html.setAttribute('data-theme-version', theme.versions[0]);
                } else {
                    html.removeAttribute('data-theme-version');
                }
            }
        }

        // Persist to database for authenticated users
        if (isAuthenticated) {
            try {
                await axios.patch('/q/api/user-preferences', {
                    selectedTheme: theme.name === 'default' ? null : theme.name,
                    selectedThemeVersion: theme.versions?.[0] || null
                });
            } catch (error) {
                // Silently fail - localStorage/Redux still has the preference
                console.warn('Failed to persist theme preference:', error.message);
            }
        }

        // Also save to localStorage as fallback
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('selectedTheme', theme.name);
        }

        setIsOpen(false);
    };

    return (
        <div ref={dropdownRef} className={`theme-picker relative ${className}`}>
            {/* Trigger Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="theme-picker-trigger flex items-center gap-2 px-3 py-2 rounded-lg
                           bg-theme-surface/80 dark:bg-theme-surface/80
                           border border-theme-border dark:border-theme-border
                           hover:bg-theme-surface-hover dark:hover:bg-theme-surface-hover
                           transition-colors duration-200 text-sm font-medium"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                <span className="text-lg">{currentTheme.icon}</span>
                <span className="hidden sm:inline text-theme-text-muted dark:text-theme-text-muted">
                    {currentTheme.label}
                </span>
                <svg
                    className={`w-4 h-4 text-theme-text-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className="theme-picker-dropdown absolute right-0 mt-2 w-56 py-2
                               bg-theme-surface dark:bg-theme-surface
                               border border-theme-border dark:border-theme-border
                               rounded-xl shadow-lg z-50
                               animate-in fade-in slide-in-from-top-2 duration-200"
                    role="listbox"
                    aria-label="Select theme"
                >
                    {AVAILABLE_THEMES.map((theme) => (
                        <button
                            key={theme.name}
                            onClick={() => handleThemeSelect(theme)}
                            className={`w-full px-4 py-3 flex items-start gap-3 text-left
                                       hover:bg-theme-surface-hover dark:hover:bg-theme-surface-hover
                                       transition-colors duration-150
                                       ${currentThemeName === theme.name ? 'bg-theme-primary/10 dark:bg-theme-primary/10' : ''}`}
                            role="option"
                            aria-selected={currentThemeName === theme.name}
                        >
                            <span className="text-2xl flex-shrink-0">{theme.icon}</span>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-theme-text dark:text-theme-text">
                                        {theme.label}
                                    </span>
                                    {currentThemeName === theme.name && (
                                        <svg className="w-4 h-4 text-theme-primary" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                                <p className="text-xs text-theme-text-muted dark:text-theme-text-muted mt-0.5">
                                    {theme.description}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
